import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailSetup } from '../../dto/trail-setup';
import { TrailSetupService } from '../../service/trail-setup.service';
import { Trail } from '../../dto/trail';
import { TrailSetupDetailsComponent } from '../trail-setup-details/trail-setup-details.component';

@Component({
  selector: 'dpi-trail-setup',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-setup.component.html',
  styleUrl: './trail-setup.component.scss'
})
export class TrailSetupComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['system_setup', 'remark', 'confirmed'];
  trail: Trail;
  dataSource: TrailSetup[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailSetupService: TrailSetupService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.reload(model);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailSetupService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.confirmed) {
              obj.confirmed_text = 'Yes';
            } else {
              obj.confirmed_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailSetup(selected: TrailSetup) {
    if (!this.isReadonly) {
      var objCopy: TrailSetup = { ...selected };
      const dialogRef = this._dialog.open(TrailSetupDetailsComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.updateTrailSetup(dialogRef.componentInstance.trailSetup);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.deleteTrailSetup(dialogRef.componentInstance.trailSetup.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  createTrailSetup() {
    this.trailSetupService.createTrialSetup(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailSetup(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailSetup) {
    let list: TrailSetup[] = [];
    for (var item of this.dataSource) {
      if (item.confirmed) {
        item.confirmed_text = 'Yes';
      } else {
        item.confirmed_text = 'No';
      }
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  updateTrailSetup(setup: TrailSetup) {
    this.trailSetupService.updateTrialSetup(setup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.replaceModelInDataSource(model);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailSetup) {
    let list: TrailSetup[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        if (model.confirmed) {
          model.confirmed_text = 'Yes';
        } else {
          model.confirmed_text = 'No';
        }
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  deleteTrailSetup(trailSetupId: number) {
    this.trailSetupService.deleteTrialSetup(trailSetupId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(trailSetupId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(trailSetupId: number) {
    let list: TrailSetup[] = [];
    for (var item of this.dataSource) {
      if (item.id === trailSetupId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}
